<template>
  <template v-if="integrations?.length">
    <div
      class="tw:flex tw:flex-row tw:justify-between tw:border-0 tw:border-b tw:border-solid tw:border-gray-200 tw:p-4"
    >
      <div class="tw:flex">
        <InputText
          v-model="filters.label"
          clearable
          new-design
          :placeholder="getLocaleText('global.search')"
        />
      </div>
      <div class="tw:flex tw:pr-2 items-center">
        <div class="tw:pr-2">
          {{ getLocaleText("social_integration_existing_list.sort_by") }}
        </div>
        <BaseButtonGroup>
          <BaseButton
            bordered
            class="no-pointer-events"
            :label="getLocaleText('social_integration_existing_list.name')"
            new-design
          />

          <BaseButton
            bordered
            color="white"
            :icon="sortDirectionIcon"
            icon-size="sm"
            new-design
            @click="toggleSortDirection"
          />
        </BaseButtonGroup>
      </div>
    </div>

    <div class="tw:flex tw:grow tw:flex-col tw:gap-4 tw:overflow-auto tw:p-4">
      <template v-if="!loading">
        <SocialIntegrationRow
          v-for="(integration, key) in integrations"
          :key="key"
          :integration="integration"
          show-action-sheet
          show-as-expired
          @remove-integration="emit('removeIntegration', integration)"
          @update-integration-team="emit('updateIntegrationTeam', integration)"
        />
      </template>
      <div
        v-else
        class="tw:flex tw:grow tw:flex-col tw:items-center tw:justify-center"
      >
        <QSpinner size="50" />
      </div>
    </div>

    <div
      class="tw:flex tw:h-12 tw:flex-row tw:justify-between tw:border-0 tw:border-t tw:border-solid tw:border-gray-200 tw:p-2"
    >
      <div class="tw:row tw:flex">
        <slot name="confirmation" />
      </div>

      <div class="tw:flex tw:flex-row tw:justify-end">
        <Pagination
          v-if="integrations"
          :page-size-options="PAGINATION_PAGE_SIZE_OPTIONS"
          :pagination="pagination"
          @pagination-changed="onPaginationChange"
        />
      </div>
    </div>
  </template>
  <div
    v-else
    class="tw:col tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center"
  >
    <template
      v-for="(message, index) in accountWarnings"
      :key="index"
    >
      <p
        class="tw:text-denim-900"
        :class="{
          'tw:mb-1 tw:text-base tw:font-bold': index === 0,
          'tw:text-sm': index !== 0,
        }"
      >
        {{ message }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash-es";
import { type SortScope } from "spraypaint";
import { computed, onMounted, ref, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseButtonGroup, InputText } from "shared/components/base";
import SocialIntegrationRow from "shared/components/integrations/SocialIntegrationRow.vue";
import { Pagination } from "shared/components/Pagination";
import { PLATFORM_LABELS } from "shared/constants/social";
import { OrganisationSocialIntegration } from "shared/resources";
import { type SortingOrder } from "shared/types";

interface ManageSocialIntegrationsProps {
  type: keyof typeof PLATFORM_LABELS;
}

interface PaginationOptions {
  page: number;
  pageSize: number;
  pageCount: number;
}

const PAGINATION_PAGE_SIZE_OPTIONS = [20, 40, 60];

const props = defineProps<ManageSocialIntegrationsProps>();

const integrations = ref<OrganisationSocialIntegration[]>();
const statsTotalOrganisationSocialIntegration = ref(0);
const loading = ref(false);
const page = ref(1);
const pageSize = ref(20);

const filters = ref<{ direction: SortingOrder; label: string }>({
  direction: "asc",
  label: "",
});

const pagination = computed<PaginationOptions>(() => ({
  page: page.value,
  pageSize: pageSize.value,
  pageCount: Math.ceil(
    statsTotalOrganisationSocialIntegration.value / pageSize.value
  ),
}));

onMounted(() => {
  loadExistingSocialIntegrations();
});

const emit = defineEmits<{
  removeIntegration: [OrganisationSocialIntegration];
  updateIntegrationTeam: [OrganisationSocialIntegration];
}>();

const sortDirectionIcon = computed<string>(() =>
  filters.value.direction === "asc"
    ? "fas fa-sort-amount-up"
    : "fas fa-sort-amount-down"
);

const accountWarnings = computed<string[]>(() =>
  [
    getLocaleText(`social_integration_existing_list.no_accounts`),
    getLocaleText(
      `social_integration_existing_list.connect_account_description`,
      {
        type: PLATFORM_LABELS[props.type],
      }
    ),
  ].filter(Boolean)
);

function onPaginationChange(options: PaginationOptions): void {
  page.value = options.page;
  pageSize.value = options.pageSize;

  loadExistingSocialIntegrations();
}

function toggleSortDirection(): void {
  filters.value.direction = filters.value.direction === "asc" ? "desc" : "asc";
}

function resetSearch(): void {
  page.value = 1;
  debouncedSearch();
}

const debouncedSearch = debounce(loadExistingSocialIntegrations, 300);

async function loadExistingSocialIntegrations() {
  try {
    loading.value = true;

    const query = {
      archivedAt: { eq: "null" },
      isOutreachAccount: { eq: false },
      label: {},
      socialPlatform: props.type,
    };

    if (filters.value.label) {
      query.label = {
        match: filters.value.label,
      };
    }

    const response = await OrganisationSocialIntegration.where(query)
      .order({ label: filters.value.direction } as SortScope)
      .page(page.value)
      .per(pageSize.value)
      .stats({ select_all: "integration_count" })
      .all();

    integrations.value = response.data;

    statsTotalOrganisationSocialIntegration.value =
      response.meta.stats.select_all.integration_count.length;
  } catch (error) {
    ErrorDialog(
      getLocaleText("manage_social_integrations.error_fetching"),
      error
    );
  } finally {
    loading.value = false;
  }
}

watch(() => filters, resetSearch, { deep: true });

defineExpose({
  reload: loadExistingSocialIntegrations,
  fetchedIntegrations: integrations,
});
</script>
